import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { navigateTo } from '../../../libs/history';
import { getTrackedInternalLinkUrl } from '../../../libs/tracking';
import { formatCountdownMsTime } from '../../../libs/utils';
import { useTrackedImpression } from '../../../scripts/hooks/useTrackedImpression';
import { asset } from '../../../services/assetResolverService';
import { useMedia } from '../../grid/hooks';
import { Link } from '../../link';
import CountdownTimer from '../../presentational/countdownTimer';
import { Typography } from '../../typography';

type TMdiMidnightSeasonFinalsUpsellProps = {
  className?: string;
};

type TFinalsClickPart = 'body' | 'button' | 'bracket';

type TUpsellTranslations = {
  watchPrompt: string;
  countdownLabel: string;
  countdownCompleted: string;
  supportingLine: string;
  button: string;
  bracketLink: string;
  logoAlt: string;
};

const NAMESPACE = 'dyn_mdi_midnight_season_finals_upsell';
const ANALYTICS_PREFIX = 'mdi_midnight_season_finals_upsell';
const ANALYTICS_KEY = 'season_finals';
const LOGO_PATH = '/images/events/expansion11/mdi-midnight-season-1/mdi_logo.png';
const BACKGROUND_PATH = '/images/events/expansion11/mdi-midnight-season-1/mdi_bg.jpg';
const WATCH_URL = '/tournaments/mdi';
const BRACKET_URL = '/events/mdi-midnight-season-1/brackets/season-finals';
// End of the Season Finals weekend; the "Ends in" countdown ticks down to this, and the
// rotation window in index.tsx reuses it as the upsell's `endAt` (single source of truth).
export const ENDS_AT = '2026-06-15T02:00:00Z';

const EN_TRANSLATIONS: TUpsellTranslations = {
  watchPrompt: 'Watch the MDI Season Finals Live',
  countdownLabel: 'Ends in',
  countdownCompleted: 'Completed',
  supportingLine:
    "<0>The season's best teams are battling for the title!</0> Watch the <1>MDI Season Finals</1> live and <0>follow every series</0>.",
  button: 'WATCH LIVE',
  bracketLink: 'View Season Finals Bracket',
  logoAlt: 'Mythic Dungeon International logo'
};

const UPSELL_TRANSLATIONS: Record<string, TUpsellTranslations> = {
  en: EN_TRANSLATIONS,
  'en-us': EN_TRANSLATIONS
};

const panelStyle: React.CSSProperties = {
  border: '1px solid rgba(86, 202, 255, 0.78)',
  borderRadius: '0.25rem',
  backgroundColor: '#061327',
  backgroundSize: 'cover',
  backgroundPosition: 'bottom center',
  backgroundRepeat: 'no-repeat',
  color: '#fff',
  cursor: 'pointer',
  columnGap: '0.75rem',
  overflow: 'hidden'
};

const logoWrapStyle: React.CSSProperties = {
  width: 124,
  minWidth: 124,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginLeft: '-0.1rem'
};

const logoStyle: React.CSSProperties = {
  display: 'block',
  width: 118,
  height: 118,
  objectFit: 'contain',
  filter: 'drop-shadow(0 8px 18px rgba(0, 0, 0, 0.34))'
};

const contentStyle: React.CSSProperties = {
  flex: '1 1 auto',
  maxWidth: '48rem',
  minWidth: 0
};

const headerRowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: '0.625rem',
  marginBottom: '0.38rem'
};

const liveWatchPromptStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '0.32rem',
  color: '#ffe29a',
  lineHeight: 1.1
};

const countdownStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '0.35rem',
  padding: '0.2rem 0.55rem',
  borderRadius: '999px',
  border: '1px solid rgba(96, 209, 255, 0.45)',
  background: 'rgba(7, 15, 40, 0.82)',
  color: '#8fdfff',
  fontSize: '0.8125rem',
  fontWeight: 'bold'
};

const supportingLineStyle: React.CSSProperties = {
  lineHeight: 1.18,
  maxWidth: '39rem',
  color: '#e7f6fb'
};

const buttonGroupStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '0.35rem',
  alignItems: 'stretch'
};

const primaryButtonStyle: React.CSSProperties = {
  backgroundColor: '#ce9908',
  minWidth: 156
};

const supportLinkStyle: React.CSSProperties = {
  color: 'rgba(218, 246, 255, 0.93)',
  textAlign: 'center',
  textShadow: '0 0 6px rgba(91, 211, 255, 0.12)'
};

const ctaButtonClassName = [
  'slds-button',
  'slds-button--raiderio',
  'text-white',
  'slds-p-vertical--x-small',
  'slds-p-horizontal--small',
  'text-bold',
  'rio-text--title',
  'slds-text-heading--medium',
  'text-nowrap'
].join(' ');

const secondaryLinkClassName = ['slds-text-body--small', 'text-bold', 'rio-realm-link'].join(' ');

export function getMdiMidnightSeasonFinalsUpsellClickTrackingPayload(
  part: TFinalsClickPart,
  linkText: string,
  linkClasses = ''
) {
  const destinationPath = part === 'bracket' ? BRACKET_URL : WATCH_URL;

  return {
    link_url: getTrackedInternalLinkUrl(destinationPath),
    link_text: linkText,
    link_classes: linkClasses,
    link_id: `${ANALYTICS_PREFIX}:${part}:${ANALYTICS_KEY}`,
    content_type: 'upsell',
    content_id: `${ANALYTICS_PREFIX}:${ANALYTICS_KEY}`,
    upsell_id: ANALYTICS_PREFIX,
    upsell_part: part,
    upsell_variant: ANALYTICS_KEY,
    upsell_status: 'live',
    destination_path: destinationPath
  };
}

export function MdiMidnightSeasonFinalsUpsell(props: TMdiMidnightSeasonFinalsUpsellProps) {
  const { t, i18n } = useTranslation();
  const isMobile = useMedia(['(max-width: 767px)'], [true], false);
  const isStackedCta = useMedia(['(max-width: 1099px)'], [true], false);
  const shouldStackCta = !isMobile && isStackedCta;
  const [isCountdownComplete, setIsCountdownComplete] = useState(() => Date.parse(ENDS_AT) <= Date.now());

  Object.entries(UPSELL_TRANSLATIONS).forEach(([lang, translations]) => {
    if (!i18n.hasResourceBundle(lang, NAMESPACE)) {
      i18n.addResourceBundle(lang, NAMESPACE, translations);
    }
  });

  const watchPromptText = t(`${NAMESPACE}:watchPrompt`);
  const primaryButtonLabel = t(`${NAMESPACE}:button`);
  const bracketLinkLabel = t(`${NAMESPACE}:bracketLink`);
  const panelImpressionRef = useTrackedImpression<HTMLDivElement>({
    onImpression: () => {
      RIO_TRACK_EVENT('event', 'upsell_impression', {
        content_type: 'upsell',
        content_id: `${ANALYTICS_PREFIX}:${ANALYTICS_KEY}`,
        upsell_id: ANALYTICS_PREFIX,
        upsell_variant: ANALYTICS_KEY,
        upsell_status: 'live',
        destination_path: WATCH_URL
      });
    }
  });

  const panelBackgroundStyle = {
    ...panelStyle,
    backgroundImage: [
      'radial-gradient(circle at 88% 45%, rgba(86, 202, 255, 0.18) 0%, rgba(86, 202, 255, 0.05) 24%, rgba(86, 202, 255, 0) 50%)',
      'linear-gradient(90deg, rgba(3, 8, 20, 0.97) 0%, rgba(6, 20, 43, 0.94) 37%, rgba(7, 36, 66, 0.76) 68%, rgba(4, 10, 20, 0.76) 100%)',
      `url(${asset(BACKGROUND_PATH, { width: 1800, height: 360, quality: 92 })})`
    ].join(', ')
  };
  const panelResponsiveStyle = {
    ...panelBackgroundStyle,
    justifyContent: isMobile ? 'center' : undefined,
    rowGap: isMobile ? '0.5rem' : undefined
  };
  const logoWrapResponsiveStyle = {
    ...logoWrapStyle,
    marginLeft: isMobile ? 0 : logoWrapStyle.marginLeft
  };
  const logoResponsiveStyle: React.CSSProperties = {
    ...logoStyle,
    width: isMobile ? 108 : logoStyle.width,
    height: isMobile ? 108 : logoStyle.height
  };
  const contentResponsiveStyle: React.CSSProperties = {
    ...contentStyle,
    textAlign: isMobile ? 'center' : 'left',
    maxWidth: shouldStackCta ? '100%' : contentStyle.maxWidth,
    margin: isMobile ? '0 auto' : undefined
  };
  const headerRowResponsiveStyle: React.CSSProperties = {
    ...headerRowStyle,
    justifyContent: isMobile ? 'center' : 'flex-start'
  };
  const supportingLineResponsiveStyle: React.CSSProperties = {
    ...supportingLineStyle,
    margin: isMobile ? '0 auto' : undefined
  };
  const buttonColumnStyle: React.CSSProperties = {
    flex: isMobile || shouldStackCta ? '0 0 100%' : '0 0 174px',
    width: isMobile || shouldStackCta ? '100%' : 174,
    textAlign: isMobile || shouldStackCta ? 'center' : undefined
  };
  const buttonGroupResponsiveStyle = {
    ...buttonGroupStyle,
    alignItems: isMobile || shouldStackCta ? 'center' : 'stretch',
    transform: isMobile || shouldStackCta ? undefined : 'translateX(-0.35rem)'
  };

  const trackUpsellClick = (e: React.MouseEvent<HTMLElement>, part: TFinalsClickPart, linkText: string) => {
    const trackingPayload = getMdiMidnightSeasonFinalsUpsellClickTrackingPayload(
      part,
      linkText,
      e.currentTarget.className || ''
    );

    RIO_TRACK_EVENT('event', 'upsell_click', trackingPayload);
    return trackingPayload;
  };

  const trackBodyClick = (e: React.MouseEvent<HTMLElement>) => {
    const trackingPayload = trackUpsellClick(e, 'body', watchPromptText);

    RIO_TRACK_EVENT('event', 'internal_click', trackingPayload);
  };

  const handlePanelClick = (e: React.MouseEvent<HTMLDivElement>) => {
    trackBodyClick(e);
    navigateTo(WATCH_URL, e);
  };

  const handleTextClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    trackBodyClick(e);
    navigateTo(WATCH_URL, e);
  };

  return (
    <div className={props.className}>
      <div
        ref={panelImpressionRef}
        className="slds-p-around--x-small slds-grid slds-grid--vertical-align-center slds-wrap"
        onClick={handlePanelClick}
        style={panelResponsiveStyle}
      >
        <div className="slds-col slds-shrink-none slds-align--absolute-center rio-m-top-mobi--small">
          <span style={logoWrapResponsiveStyle}>
            <img
              src={asset(LOGO_PATH, { width: 236, height: 236 })}
              alt={t(`${NAMESPACE}:logoAlt`)}
              width={118}
              height={118}
              style={logoResponsiveStyle}
            />
          </span>
        </div>
        <div
          className="slds-col rio-m-top-mobi--small slds-p-horizontal--small"
          onClick={handleTextClick}
          style={contentResponsiveStyle}
        >
          <div style={headerRowResponsiveStyle}>
            <Typography.Title component="span" size="medium" strong style={liveWatchPromptStyle}>
              <i className="fa fa-play-circle" />
              {watchPromptText}
            </Typography.Title>
            <span style={countdownStyle}>
              <i className="fa fa-stopwatch" />
              {isCountdownComplete ? (
                <span className="text-white">{t(`${NAMESPACE}:countdownCompleted`)}</span>
              ) : (
                <>
                  <span>{t(`${NAMESPACE}:countdownLabel`)}</span>
                  <CountdownTimer
                    className="text-white"
                    to={ENDS_AT}
                    formatter={formatCountdownMsTime}
                    onComplete={() => setIsCountdownComplete(true)}
                    tag="span"
                  />
                </>
              )}
            </span>
          </div>
          <Typography.Text component="div" size="large" style={supportingLineResponsiveStyle}>
            <Trans i18nKey={`${NAMESPACE}:supportingLine`} components={[<b />, <b />, <b />]} />
          </Typography.Text>
        </div>
        <div
          className="slds-col slds-grow slds-p-vertical--small slds-p-right--small slds-align--absolute-center rio-m-top-mobi--small"
          style={buttonColumnStyle}
        >
          <div style={buttonGroupResponsiveStyle}>
            <Link
              className={ctaButtonClassName}
              to={WATCH_URL}
              as={`${ANALYTICS_PREFIX}:button:${ANALYTICS_KEY}`}
              onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
                e.stopPropagation();
                trackUpsellClick(e, 'button', primaryButtonLabel);
              }}
              style={primaryButtonStyle}
            >
              {primaryButtonLabel}
            </Link>
            <Link
              className={secondaryLinkClassName}
              to={BRACKET_URL}
              as={`${ANALYTICS_PREFIX}:bracket:${ANALYTICS_KEY}`}
              onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
                e.stopPropagation();
                trackUpsellClick(e, 'bracket', bracketLinkLabel);
              }}
              style={supportLinkStyle}
            >
              {bracketLinkLabel}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
